import { useEffect, useState } from "react";
import Tippy from "@tippyjs/react";
import { Pin, PinOff } from "lucide-react";
import { useChat } from "./ChatProvider";
import { fetchPinnedMessages } from "../../controllers/chatController";
import { formatListTime, messagePreview, userName } from "./chatUtils";

// Bar under the conversation header showing the latest pinned message; "All" lists every pin.
// Refetches whenever a pin in this conversation changes (pinsVersion is bumped by the provider).
function PinnedBar({ conversationId, compact }) {
  const { users, me, pinsVersion, focusMessage, setPinned } = useChat();
  const [pins, setPins] = useState([]);
  const [open, setOpen] = useState(false);
  const version = pinsVersion[conversationId] || 0;

  useEffect(() => {
    let cancelled = false;
    fetchPinnedMessages(conversationId)
      .then((list) => !cancelled && setPins(Array.isArray(list) ? list : []))
      .catch(() => !cancelled && setPins([]));
    return () => {
      cancelled = true;
    };
  }, [conversationId, version]);

  if (!pins.length) return null;
  const [latest] = pins;
  const preview = (message) => messagePreview({ ...message, senderId: null }, users, me?.id);
  const jump = (message) => {
    setOpen(false);
    focusMessage(conversationId, message.id);
  };

  const list = (
    <div className="w-80 max-h-80 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-xl py-1">
      <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{pins.length} pinned</p>
      {pins.map((message) => (
        <div key={message.id} className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700">
          <button type="button" onClick={() => jump(message)} className="min-w-0 flex-1 text-left">
            <span className="block text-xs text-gray-500">
              {message.senderId === me?.id ? "You" : userName(users[message.senderId])} · {formatListTime(message.createdAt)}
            </span>
            <span className="block text-sm line-clamp-2 break-words">{preview(message)}</span>
          </button>
          <button
            type="button"
            onClick={() => setPinned(message.id, false).catch(() => {})}
            className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-600"
            title="Unpin"
            aria-label="Unpin message"
          >
            <PinOff className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`flex items-center gap-2 border-b border-amber-200/70 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 px-3 ${compact ? "py-1.5" : "py-2"}`} data-chat-pinned-bar>
      <Pin className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <button type="button" onClick={() => jump(latest)} className="min-w-0 flex-1 text-left" title="Go to pinned message">
        <span className="block text-[11px] font-semibold text-amber-700 dark:text-amber-300">Pinned message</span>
        <span className="block truncate text-xs text-gray-700 dark:text-gray-200">{preview(latest)}</span>
      </button>
      {pins.length > 1 && (
        <Tippy
          theme="irg-plain"
          arrow={false}
          visible={open}
          onClickOutside={() => setOpen(false)}
          interactive
          placement="bottom-end"
          appendTo={() => document.body}
          zIndex={90}
          content={list}
        >
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
          >
            All ({pins.length})
          </button>
        </Tippy>
      )}
    </div>
  );
}

export default PinnedBar;
